import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Clock, Check, X, Ban, Play, Loader2, Receipt, BellRing } from "lucide-react";
import api, { apiError } from "../../../lib/api";
import { fmtDate, fmtDateTime } from "../../../lib/time";
import { Label, StatCard } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";

export default function PlatformBilling() {
  const [d, setD] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [form, setForm] = useState({ amount: "", period_start: "", period_end: "", method: "EFT", reference: "" });
  const [preview, setPreview] = useState(null);

  const reminders = async (dry) => {
    setBusy(dry ? "preview" : "send");
    try {
      const { data } = await api.post(`/platform/billing/run-reminders?dry_run=${dry}`);
      if (dry) setPreview(data);
      else { toast.success(`${data.reminders.length} reminder${data.reminders.length === 1 ? "" : "s"} sent`); setPreview(null); await load(); }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const load = async () => {
    try {
      const [b, r] = await Promise.all([api.get("/platform/billing"), api.get("/platform/subscription-requests")]);
      setD(b.data); setReqs(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const resolve = async (id, action) => {
    setBusy(id + action);
    try { await api.post(`/platform/subscription-requests/${id}/resolve`, { action }); toast.success(`Request ${action}d`); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const setStatus = async (orgId, status) => {
    setBusy(orgId + status);
    try { await api.patch(`/platform/organizations/${orgId}/status`, { status }); toast.success(status === "suspended" ? "Access suspended" : "Access restored"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const recordPayment = async () => {
    if (!form.amount || !form.period_start || !form.period_end) { toast.error("Amount and period are required"); return; }
    setBusy("pay");
    try {
      await api.post(`/platform/organizations/${payFor.org_id}/payments`, {
        amount: parseFloat(form.amount), period_start: form.period_start, period_end: form.period_end,
        method: form.method, reference: form.reference });
      toast.success("Payment recorded — org marked paid up");
      setPayFor(null); setForm({ amount: "", period_start: "", period_end: "", method: "EFT", reference: "" });
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (!d) return <div className="text-slate-400">Loading billing ledger…</div>;
  const cur = (n) => `R${Number(n || 0).toLocaleString()}`;
  const pending = reqs.filter((r) => r.status === "pending");
  const badge = { overdue: "bg-red-100 text-red-700", due_soon: "bg-orange-100 text-orange-700", awaiting_payment: "bg-amber-100 text-amber-700", suspended: "bg-slate-800 text-white", active: "bg-emerald-100 text-emerald-700" };
  const badgeTxt = { overdue: "Overdue", due_soon: "Due soon", awaiting_payment: "Awaiting payment", suspended: "Suspended", active: "Active" };

  return (
    <div className="space-y-6" data-testid="platform-billing">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><Label>Finance</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Billing & Payments</h1></div>
        <Button variant="outline" onClick={() => reminders(true)} disabled={!!busy} data-testid="check-reminders">
          {busy === "preview" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <BellRing className="mr-1 h-4 w-4" />}Payment reminders
        </Button>
      </div>

      {preview && <ReminderPreview p={preview} busy={busy} onSend={() => reminders(false)} onClose={() => setPreview(null)} />}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Overdue" value={d.overdue_count} accent="#EF4444" testId="bill-overdue" />
        <StatCard label="Due in 7 days" value={d.due_soon_count} accent="#EA580C" testId="bill-due-soon" />
        <StatCard label="Awaiting payment" value={d.awaiting_count} accent="#F59E0B" testId="bill-awaiting" />
        <StatCard label="MRR at risk" value={cur(d.mrr_at_risk)} accent="#EF4444" testId="bill-mrr-risk" />
        <StatCard label="Pending requests" value={d.pending_requests} accent="#4338CA" testId="bill-pending" />
      </div>

      {pending.length > 0 && (
        <div className="rounded-xl border border-indigo-200 bg-white p-5" data-testid="subscription-requests">
          <Label>Subscription requests</Label>
          <div className="mt-3 space-y-2">
            {pending.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-indigo-50/60 p-3" data-testid={`req-${r.id}`}>
                <div className="text-sm"><b>{r.org_name}</b> → <b className="text-indigo-700">{r.requested_plan_name}</b> <span className="text-slate-500">({cur(r.price)}/mo) · by {r.requested_by_name}</span></div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => resolve(r.id, "approve")} disabled={!!busy} data-testid={`req-approve-${r.id}`} className="bg-emerald-600 hover:bg-emerald-700">
                    {busy === r.id + "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-1 h-4 w-4" />Approve</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => resolve(r.id, "decline")} disabled={!!busy} data-testid={`req-decline-${r.id}`} className="text-red-600">
                    <X className="mr-1 h-4 w-4" />Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="billing-orgs">
        <Label>Organisations</Label>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
              <tr><th className="py-2">Organisation</th><th>Plan</th><th>MRR</th><th>Status</th><th>Paid until</th><th className="text-right">Actions</th></tr>
            </thead>
            <tbody>
              {d.orgs.map((o) => (
                <tr key={o.org_id} className="border-t border-slate-100" data-testid={`bill-row-${o.org_id}`}>
                  <td className="py-2 font-semibold text-slate-800">{o.org_name}</td>
                  <td>{o.plan_name}</td>
                  <td>{o.mrr ? cur(o.mrr) : "—"}</td>
                  <td data-testid={`bill-state-${o.org_id}`}><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${badge[o.state]}`}>{badgeTxt[o.state]}</span>
                    {(o.state === "due_soon" || o.state === "overdue") && o.days_left != null && <span className="ml-1.5 text-xs font-semibold text-slate-500" data-testid={`bill-days-${o.org_id}`}>{daysText(o.days_left)}</span>}
                    {o.last_reminder && <p className="mt-0.5 text-[11px] text-slate-400" data-testid={`bill-reminded-${o.org_id}`}>Reminded ({STAGE_TXT[o.last_reminder.stage]}) {fmtDate(o.last_reminder.sent_at)}</p>}</td>
                  <td className="text-slate-500">{o.paid_until ? fmtDate(o.paid_until) : "—"}</td>
                  <td className="py-2">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => { setPayFor(o); }} data-testid={`record-payment-${o.org_id}`}>
                        <Receipt className="mr-1 h-4 w-4" />Record payment
                      </Button>
                      {o.org_status === "suspended" ? (
                        <Button size="sm" variant="outline" onClick={() => setStatus(o.org_id, "active")} disabled={!!busy} data-testid={`restore-${o.org_id}`} className="text-emerald-600">
                          <Play className="mr-1 h-4 w-4" />Restore
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setStatus(o.org_id, "suspended")} disabled={!!busy} data-testid={`suspend-${o.org_id}`} className="text-red-600">
                          <Ban className="mr-1 h-4 w-4" />Suspend
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {payFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setPayFor(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="payment-modal">
            <h3 className="font-head text-lg font-bold text-slate-900">Record payment · {payFor.org_name}</h3>
            <div className="mt-4 space-y-3">
              <Input type="number" placeholder="Amount (ZAR)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-amount" />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500">Period start</label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="pay-start" /></div>
                <div><label className="text-xs text-slate-500">Period end</label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="pay-end" /></div>
              </div>
              <Input placeholder="Method (e.g. EFT)" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="pay-method" />
              <Input placeholder="Reference (optional)" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="pay-ref" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPayFor(null)}>Cancel</Button>
              <Button onClick={recordPayment} disabled={busy === "pay"} data-testid="pay-save" className="bg-[#4338CA] hover:bg-[#3730A3]">
                {busy === "pay" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mark paid"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {d.recent_payments.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="recent-payments">
          <Label>Recent payments</Label>
          <table className="mt-2 w-full text-sm">
            <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
              <tr><th className="py-1">Date</th><th>Org</th><th>Amount</th><th>Period</th><th>By</th></tr>
            </thead>
            <tbody>
              {d.recent_payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="py-1.5">{fmtDateTime(p.created_at)}</td><td>{p.org_name}</td>
                  <td className="font-semibold">{cur(p.amount)}</td>
                  <td className="text-slate-500">{fmtDate(p.period_start)} → {fmtDate(p.period_end)}</td>
                  <td className="text-slate-400">{p.recorded_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


const STAGE_TXT = { "7d": "7 days before", "3d": "3 days before", today: "due today", overdue: "overdue" };
const daysText = (n) => (n > 1 ? `in ${n} days` : n === 1 ? "tomorrow" : n === 0 ? "today" : `${-n} day${n === -1 ? "" : "s"} late`);

function ReminderPreview({ p, busy, onSend, onClose }) {
  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-5" data-testid="reminder-preview">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-head font-bold text-slate-900">Payment reminders due now</p>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="reminder-preview-close"><X className="h-4 w-4" /></button>
      </div>
      <p className="mt-1 text-sm text-slate-600">Runs automatically every day at 08:00 SAST: corporate admins are emailed 7 and 3 days before, on the day, and once after it lapses. Master &amp; Finance get a daily summary.</p>
      {!p.mail_configured && <p className="mt-2 text-sm font-semibold text-red-600">Email is not configured — reminders can't be sent.</p>}
      {p.reminders.length === 0 ? <p className="mt-3 text-sm font-semibold text-emerald-700" data-testid="reminder-none">No new reminders to send right now — every due reminder has already gone out.</p> : (
        <ul className="mt-3 space-y-1.5 text-sm" data-testid="reminder-list">
          {p.reminders.map((r) => (
            <li key={r.org_id} className="flex flex-wrap justify-between gap-2 rounded-lg bg-white px-3 py-2">
              <span><b>{r.org_name}</b> · {STAGE_TXT[r.stage]} ({daysText(r.days_left)})</span>
              <span className="text-slate-500">{r.recipients.length ? r.recipients.join(", ") : "No corporate admin email"}</span>
            </li>
          ))}
        </ul>
      )}
      {p.reminders.length > 0 && p.mail_configured && (
        <Button onClick={onSend} disabled={!!busy} className="mt-3 bg-[#4338CA] hover:bg-[#3730A3]" data-testid="send-reminders">
          {busy === "send" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <BellRing className="mr-1 h-4 w-4" />}Send these reminders now
        </Button>
      )}
    </div>
  );
}
